import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Api from "../../../../shared/api/api";
import YouTubeChannelButton from "../../../media/YouTubeChannelButton";
import { trackEvent } from "../../../../shared/helpers/analytics";
import "../../styles/YouTubeSection.css";

const newestFirst = (a, b) => (Date.parse(b.publishedAt) || 0) - (Date.parse(a.publishedAt) || 0);
export default function YouTubeSection({ scope = "author", title = "Author Videos & Shorts", preview = true }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const generation = useRef(0), busy = useRef(false);
  const base = scope === "entertainment" ? "/avwakinentertainment" : "/blog";
  useEffect(() => {
    const current = ++generation.current;
    busy.current = true; setResult(null); setError(""); setLoading(true);
    Api.request("youtube", { scope, feed: "uploads" }).then(data => {
      if (generation.current === current) setResult({ ...data, videos: [...data.videos].sort(newestFirst) });
    }).catch(() => { if (generation.current === current) setError("YouTube videos could not be loaded. Please try again."); })
      .finally(() => { if (generation.current === current) { setLoading(false); busy.current = false; } });
    return () => { generation.current++; };
  }, [scope, retry]);
  async function loadOlder() {
    if (busy.current || !result?.nextPageToken) return;
    const current = generation.current; busy.current = true; setLoading(true); setError("");
    try {
      const data = await Api.request("youtube", { scope, feed: "uploads", pageToken: result.nextPageToken });
      if (generation.current !== current) return;
      setResult(previous => {
        const videos = new Map(previous.videos.map(video => [video.id, video]));
        data.videos.forEach(video => videos.set(video.id, video));
        return { ...data, videos: [...videos.values()].sort(newestFirst) };
      });
    } catch { if (generation.current === current) setError("More videos could not be loaded. Please try again."); }
    finally { if (generation.current === current) { setLoading(false); busy.current = false; } }
  }
  const videos = preview ? result?.videos.slice(0, 6) : result?.videos;
  return <section className="youtubeSection" aria-labelledby={`youtube-heading-${scope}`}>
    <div className="youtubeHeading"><h2 id={`youtube-heading-${scope}`}>{title}</h2><YouTubeChannelButton scope={scope} /></div>
    <p>Videos & Shorts together · Newest first</p>
    {error && <div role="alert"><p>{error}</p><button type="button" disabled={loading} onClick={() => result?.nextPageToken && !preview ? loadOlder() : setRetry(value => value + 1)}>Try again</button></div>}
    {loading && <p role="status">Loading videos…</p>}
    {!loading && !error && result?.videos.length === 0 && <p className="youtubeEmpty">{result.status === "not-configured" ? "Videos & Shorts will appear here soon." : "No public videos are available yet."}</p>}
    <div className="youtubeGrid">{(videos || []).map(video => <article className="youtubeCard" key={video.id}>
      <Link className="youtubeTileLink" to={`${base}/video/${video.id}`} onClick={() => { if (scope === "entertainment") trackEvent("entertainment_video_click"); }} aria-label={`Watch ${video.title}`}>
        <div className="youtubeMedia"><div className="youtubePlay">{video.thumbnail && <img src={video.thumbnail} alt="" loading="lazy" />}<span aria-hidden="true">▶</span></div></div>
        <div className="youtubeCardBody"><h3>{video.title}</h3>{video.publishedAt && <time dateTime={video.publishedAt}>{new Date(video.publishedAt).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</time>}<p>{video.description?.slice(0, 180)}{video.description?.length > 180 ? "…" : ""}</p><span className="youtubeTileAction">Watch video →</span></div>
      </Link>
    </article>)}</div>
    {preview && Boolean(result?.videos.length) && <Link className="youtubeLoadMore" to={`${base}/videos`}>Load more videos</Link>}
    {!preview && result?.nextPageToken && <button type="button" className="youtubeLoadMore" disabled={loading} onClick={loadOlder}>{loading ? "Loading…" : "Load older videos"}</button>}
  </section>;
}
